import { bootQaMode, type FeedbackSetting, type QaMode, type QaModeHost } from './qa-mode.ts';

/**
 * The QA mode's boot, shared by every page of the web app (the console, the candidate's exam and
 * the public boleta «Quiero ser agente»): reads /config.json (`feedback`: false in production, so
 * nothing loads there), honors `?mode=qa|normal` and its aliases, the choice remembered in this
 * browser, and loads /__feedback/widget.js. Pages without a «Modo QA» button need nothing else:
 * the widget shows its own launcher. A test walks every src/*.html and fails if one does not
 * start it (apps/web/test/qa-boot.test.ts).
 */

/** This browser tab, as the QA mode sees it. */
function browserHost(explain: (message: string) => void): QaModeHost {
  return {
    get url() {
      return location.href;
    },
    get storage() {
      try {
        return localStorage;
      } catch {
        return null;
      }
    },
    loadWidget: (src) =>
      document.head.append(Object.assign(document.createElement('script'), { src, defer: true })),
    replaceUrl: (url) => history.replaceState(history.state, '', url),
    navigate: (url) => location.assign(url),
    explain,
  };
}

/** Public: no session, no cookies, never cached (it differs per environment). */
const fetchConfig = async (): Promise<{ feedback?: FeedbackSetting }> =>
  (await fetch('/config.json', { cache: 'no-store', credentials: 'omit' })).json();

/**
 * Starts the QA mode on this page. The console passes the /config.json it already read and how
 * to tell the person something (its toast); the other pages pass nothing.
 */
export function startQaMode(
  options: { config?: { feedback?: FeedbackSetting }; explain?: (message: string) => void } = {},
): Promise<QaMode> {
  const { config, explain = () => {} } = options;
  return bootQaMode(config ? async () => config : fetchConfig, browserHost(explain));
}
